﻿import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Footer from '../Footers/Footer';
import Header from '../Headers/Header';
import Seo from '../Shared/components/Seo.jsx';
import { setAuth, setRole } from '../Redux/Actions/AuthActions';
import LogInForm from './LogInForm';
import AuthMainImage from './AuthMainImage.jsx';
import Popup from './Popup';
import ErrorNotifier from "./Notifications/ErrorNotifier.jsx";
import SuccessNotifier from "./Notifications/SuccessNotifier.jsx";
import { isAbortError } from './isAbortError.js';
import { useAbortController } from '../Shared/hooks/useAbortController.js';
import {
    appendReturnTo,
    clearAuthReturnTo,
    readAuthReturnTo,
} from '../Shared/utils/authReturnTo.js';
import { appendAuthMethod } from '../Shared/utils/authMethodQuery.js';

export default function LogInPage ({ onClose, fullPageNavigation = false }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(undefined);
    const [popupMessage, setPopupMessage] = useState('');
    const [showErrorNotifier, setShowErrorNotifier] = useState(false);
    const [showSuccessNotifier, setShowSuccessNotifier] = useState(false);
    const [errorNotifierTitle, setErrorNotifierTitle] = useState('Account not verified');
    const [errorNotifierText, setErrorNotifierText] = useState(
        'You need to verify your account before logging in. Please check your email and click the verification link',
    );
    const [errorNotifierPrimaryButtonText, setErrorNotifierPrimaryButtonText] = useState('Resend');
    const [showErrorNotifierResend, setShowErrorNotifierResend] = useState(true);
    const { nextSignal } = useAbortController();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const returnTo = readAuthReturnTo(location.search || searchParams);

    useEffect(() => {
        localStorage.removeItem("userToken");
        localStorage.removeItem("email");
        localStorage.removeItem("loginEmail");
        localStorage.removeItem("roleId");

        dispatch(setAuth(false));
    }, [dispatch]);

    useEffect(() => {
        const errorCode = searchParams.get('error');
        if (!errorCode) {
            return;
        }

        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(emailParam);
            localStorage.setItem('loginEmail', emailParam);
        }

        if (errorCode === 'account_not_verified') {
            setErrorNotifierTitle('Account not verified');
            setErrorNotifierText(
                'You need to verify your account before logging in. Please check your email and click the verification link',
            );
            setErrorNotifierPrimaryButtonText('Resend');
            setShowErrorNotifierResend(true);
            setShowErrorNotifier(true);
        } else if (errorCode === 'oauth_failed') {
            setErrorNotifierTitle('Sign in failed');
            setErrorNotifierText(
                'Something went wrong while signing in with Google or Facebook. Please try again or use email and password.',
            );
            setErrorNotifierPrimaryButtonText('Try Again');
            setShowErrorNotifierResend(false);
            setShowErrorNotifier(true);
        }

        navigate(appendReturnTo('/login', returnTo), { replace: true });
    }, [navigate, returnTo, searchParams]);

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
    };

    const handleLoginSubmit = async (loginData) => {
        const { email, password } = loginData;
        setEmail(email)
        try {
            const response = await axios.post(`${import.meta.env.VITE_BASE_URL}/account/login`, {
                email,
                password
            }, { signal: nextSignal('login') });

            if (response && response.data) {
                if (response.data.token) {
                    localStorage.setItem('userToken', response.data.token);
                    localStorage.setItem('email', email);
                    localStorage.setItem('isLoggedIn', 'true')

                    dispatch(setAuth(true));
                    const roleId = response.data.roleId;
                    if (roleId) {
                        localStorage.setItem('roleId', roleId);
                        dispatch(setRole(roleId));
                    }

                    clearAuthReturnTo();
                    const target = appendAuthMethod(returnTo || '/', 'email');
                    if (fullPageNavigation) {
                        window.location.assign(target);
                    } else {
                        navigate(target, { replace: true });
                    }
                    if (onClose) onClose();
                }
            }
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }
            if (error.response && error.response.status === 401) {
                setError({ message: 'Incorrect password. Please try again', type: 'password' });
            } else if (error.response && error.response.status === 404) {
                setError({ message: 'No account found with this email', type: 'email' });
            } else if (error.response && error.response.status === 400) {
                setErrorNotifierTitle('Account not verified');
                setErrorNotifierText(
                    'You need to verify your account before logging in. Please check your email and click the verification link',
                );
                setErrorNotifierPrimaryButtonText('Resend');
                setShowErrorNotifierResend(true);
                setShowErrorNotifier(true);
            } else if (error.response && error.response.status === 429) {
                setError({ message: `Too many failed attempts. Please try again in 15 minutes`, type: 'password' });
            } else {
                console.error('Error during login:', error);
                setError({ message: 'An unexpected error occurred. Please try again later.', type: 'general' });
            }
        }
    };

    const handleChangeToSignUp = () => {
        const target = appendReturnTo('/signup', returnTo);
        if (fullPageNavigation) {
            window.location.assign(target);
        } else {
            navigate(target);
        }
    };

    const handleClosePopup = () => {
        setPopupMessage('');
    };

    function resendVerificationEmail() {
        axios.post(`${import.meta.env.VITE_BASE_URL}/account/resend`, { email }, {
            signal: nextSignal('resend'),
        })
            .then(() => {
                setShowSuccessNotifier(true);
            })
            .catch((error) => {
                if (!isAbortError(error)) {
                    console.error('Resend verification failed:', error);
                }
            });
    }

    return (
        <main className="login-page wrapper">
            <Seo
                title="Sign In — Each Jewelry"
                description="Login to your Each Jewelry account to access exclusive features and services."
                canonical="/login"
                authHeroPreload
            />
            <Header fullPageNavigation={fullPageNavigation} />
            <LogInForm
                handleSubmit={handleLoginSubmit}
                email={email}
                password={password}
                handleEmailChange={handleEmailChange}
                handlePasswordChange={handlePasswordChange}
                error={error}
                returnTo={returnTo}
                onChangeToSignUp={handleChangeToSignUp}
                fullPageNavigation={fullPageNavigation}
            />
            <ErrorNotifier
                message={error?.message}
                type={error?.type}
                isOpened={showErrorNotifier}
                setIsOpened={setShowErrorNotifier}
                primaryButtonText={errorNotifierPrimaryButtonText}
                title={errorNotifierTitle}
                errorText={errorNotifierText}
                primaryButtonOnClick={showErrorNotifierResend ? resendVerificationEmail : undefined}
            />
            <SuccessNotifier
                isOpened={showSuccessNotifier}
                setIsOpened={setShowSuccessNotifier}
                email={email}
                type='login-resend'
            />
            <Footer fullPageNavigation={fullPageNavigation} />
            {popupMessage && <Popup message={popupMessage} onClose={handleClosePopup} />}
        </main>
    );
}